import { fontSize } from '@/hooks/use.screen.size';

export const applyOpacity = (color: string, opacity: number) => {
  if (!color.startsWith('#')) return color; // If it's already an rgba() string, leave it alone

  const cleanHex = color.replace('#', '');
  // Expand 3-digit hexes (e.g., #000 -> #000000)
  const fullHex =
    cleanHex.length === 3
      ? cleanHex
          .split('')
          .map((c) => c + c)
          .join('')
      : cleanHex;

  // Convert the 0-1 opacity into a 00-FF hex value
  const alphaHex = Math.round(opacity * 255)
    .toString(16)
    .padStart(2, '0');

  return `#${fullHex}${alphaHex}`;
};

const palette = {
  white: '#ffffff',
  whiteFaded: 'rgba(255,255,255,0.50)',
  greyWhite: '#F2F2F2',
  greyLight: '#C4C4C4',
  whiteFaded2: 'rgba(255, 255, 255, 0.1)',
  grey: '#BDBDBD',
  greyDark: '#9a9898',
  greyText: '#828282',
  blackLight: '#4f4f4f',
  black: 'black',
  jade: '#50BCBD',
  jadeLight: 'rgb(92,200,201)',
  jadeDark: '#198b8c',
  jadeVeryLight: '#E5F3F3',
  blue: '#38488A',
  blueLight: '#4e55a1',
  blueSky: '#C6E4FF',
  softBlue: '#EAF3F9',
  red: 'red',
  redDark: '#D64732',
  redSoft: '#e54e38',
  orange: '#ffab3f',
  green: '#22C55E',
  greenLight: '#3AED2A',
  shadow: 'rgb(211, 211, 211)',
  cyan: '#3498db',
};
export const theme = {
  fonts: {
    primary: 'Shabnam-FD',
    bold: 'Shabnam-Bold-FD',
    app_english: 'Shabnam',
    button: {
      primary: 'blabla',
    },
  },
  fontSizes: {
    button: {
      big: fontSize(18),
      primary: fontSize(17),
      small: fontSize(15),
    },
    text: {
      giant: fontSize(25),
      title: fontSize(19),
      medium: fontSize(15),
      primary: fontSize(12.5),
      label: fontSize(10),
      small: fontSize(9),
      chat: fontSize(15),
    },
    textField: {
      primary: fontSize(15),
      phone: fontSize(16),
    },
  },
  colors: {
    ...palette,
    error: palette.redDark,
    button: {
      disabled: { background: palette.grey, foreground: palette.black },
      primary: { background: palette.jade, foreground: palette.white },
      blue: { background: palette.blue, foreground: palette.white },
      grey: { background: palette.greyLight, foreground: palette.white },
      red: { background: palette.redSoft, foreground: palette.white },
      orange: { background: palette.orange, foreground: palette.white },
      green: { background: palette.green, foreground: palette.white },
    },
    text: {
      blue: palette.blue,
      white: palette.white,
      whiteFaded2: palette.whiteFaded2,
      faded: '#9e9e9e',
      jade: palette.jade,
      grey: palette.greyText,
      grey_light: palette.greyDark,
      black_light: palette.blackLight,
      tab_focused: palette.jade,
      tab_un_focused: palette.blue,
      red: palette.red,
      jade_dark: palette.jadeDark,
      green: palette.green,
      cyan: palette.cyan,
    },
    textField: {
      hint: 'grey',
      primary: palette.black,
      error: palette.red,
      success: palette.green,
      jade: palette.jade,
      search_bg: palette.greyWhite,
      search_border: palette.greyLight,
      label: palette.greyLight,
      white: palette.white,
      border: {
        primary: palette.jade,
        error: palette.red,
        blue: palette.blue,
      },
    },
    modal: {
      background: palette.white,
      error: palette.red,
      warn: palette.orange,
      info: palette.green,
      title: {
        background: palette.jade,
        foreground: palette.white,
      },
      backDrop: `rgba(15, 82, 83, 0.5)`,
    },
    activityIndicator: {
      primary: palette.green,
      composed: [palette.redDark, '#32AA3E', '#1A9CFC'],
    },
    connectionState: {
      background: palette.red,
    },
    indicator: {
      active: palette.blue,
      inactive: palette.greyLight,
      online: palette.green,
    },
    icon: {
      white: palette.white,
      grey_dark: palette.greyLight,
      jade: palette.jade,
      jade_dark: palette.jadeDark,
      blue: palette.blue,
    },
    listItem: {
      blue_sky: palette.blueSky,
      grey_white: palette.greyWhite,
      border: {
        grey: palette.grey,
      },
    },
    list: {
      border: {
        grey: palette.greyLight,
      },
      background: {
        primary: palette.jadeVeryLight,
      },
    },
    tab: {
      border: {
        primary: palette.greyWhite,
      },
    },
    screen: {
      background: {
        jade: palette.jade,
        white: palette.white,
      },
    },
  },
  hp: global.hp,
  wp: global.wp,
  max: global.max,
  min: global.min,
};
type CustomTheme = typeof theme;
declare module 'styled-components/native' {
  export interface DefaultTheme extends CustomTheme {}
}
